import React, { useState } from 'react';
import { X } from 'lucide-react';
import { inspectionService } from '../inspectionService';

interface CreateInspectionPlanModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateInspectionPlanModal: React.FC<CreateInspectionPlanModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [planType, setPlanType] = useState('ANNUAL');
  const [periodStart, setPeriodStart] = useState('2026-01-01');
  const [periodEnd, setPeriodEnd] = useState('2026-12-31');
  const [frequency, setFrequency] = useState('DAILY_AND_WEEKLY');
  const [ownerId, setOwnerId] = useState('USR-LEAD-INSP');
  const [riskBased, setRiskBased] = useState(true);
  const [description, setDescription] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      alert('Plan name is required.');
      return;
    }

    try {
      setLoading(true);
      await inspectionService.createPlan({
        name,
        planType,
        periodStart,
        periodEnd,
        frequency,
        ownerId,
        riskBased,
        description,
      });
      onSuccess();
    } catch (err: any) {
      alert('Failed to create inspection plan: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-lg">
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Define Field & Asset Inspection Plan</h3>
            <p>
              Create systematic surveillance schedules across aircraft, ground equipment, and ramp facilities.
            </p>
          </div>
          <button
            onClick={onClose}
            className="modal-close-btn"
            title="Kapat"
            aria-label="Kapat"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="form-field">
              <label>Plan Name / Title *</label>
              <input
                type="text"
                required
                placeholder="e.g. 2026 Annual Ramp & Line Maintenance Surveillance Plan"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Plan Horizon</label>
                <select
                  value={planType}
                  onChange={(e) => setPlanType(e.target.value)}
                >
                  <option value="ANNUAL">Annual Plan</option>
                  <option value="QUARTERLY">Quarterly Plan</option>
                  <option value="MONTHLY">Monthly Plan</option>
                  <option value="SURVEILLANCE">Continuous Surveillance</option>
                </select>
              </div>

              <div className="form-field">
                <label>Inspection Frequency</label>
                <input
                  type="text"
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                  placeholder="e.g. DAILY, WEEKLY, PER_FLIGHT"
                />
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Period Start Date *</label>
                <input
                  type="date"
                  required
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Period End Date *</label>
                <input
                  type="date"
                  required
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                />
              </div>
            </div>

            <div className="form-field">
              <label>Plan Coordinator (Owner ID) *</label>
              <input
                type="text"
                required
                value={ownerId}
                onChange={(e) => setOwnerId(e.target.value)}
              />
            </div>

            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 14px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#334155', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={riskBased}
                  onChange={(e) => setRiskBased(e.target.checked)}
                />
                <span>Apply risk-based prioritization and dynamic sampling methodology</span>
              </label>
            </div>

            <div className="form-field">
              <label>Objectives & Scope Description</label>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Scope items, aircraft fleet, line stations, hangars, and quality checkpoints..."
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{ width: 'auto' }}
            >
              {loading ? 'Saving...' : 'Save Plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateInspectionPlanModal;
